import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ExtCircService } from '../service/extCirc.service';
import { ExtCirc } from '../model/ExtCirc';
import { ReviewStatus } from '../model/ReviewStatus';
import { LayoutService } from '../../core/service/layout.service';
import { UtcDatePipe } from '../../shared/pipe/UtcDate.pipe';
import { RegistrantStatus } from '../../accounts/model/RegistrantStatus';

export interface SearchParams {
  regNumber: string;
  status: ReviewStatus | undefined;
  date?: string;
  renewalDate?: string;
}

interface ExceptionalCircumstanceListItem extends ExtCirc {
  reviewStatus: ReviewStatus;
  subject: string;
}

@Component({
    selector: 'app-ext-circ',
    templateUrl: './extCircList.component.html',
    styleUrls: ['../list.scss', './extCirc.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExtCircListComponent implements OnInit, OnDestroy {

  loading = false;
  items: ExceptionalCircumstanceListItem[] = [];
  searchVisible = false;
  assigningItem: ExceptionalCircumstanceListItem | undefined;
  ReviewStatus = ReviewStatus;
  searchCriteria: SearchParams = {
    regNumber: '',
    date: undefined,
    status: undefined,
    renewalDate: undefined
  };
  RegistrantStatus = RegistrantStatus;
  numberOfRecordsSelected = 0;
  selectedFilter = '';

  @ViewChild('regNumber') regNumberRef!: ElementRef;

  uniqueRenewalDates: string[] = [];
  dateSelectedByUser: string | undefined;
  tempItems: ExceptionalCircumstanceListItem[] = [];
  filterParams!: number;

  constructor(
    private service: ExtCircService,
    private utcDatePipe: UtcDatePipe,
    public layout: LayoutService) 
  { }

  ngOnInit() {
    const storedData = localStorage.getItem('ecList');
    const data = storedData ? JSON.parse(storedData) : {};
    this.filterParams = Object.keys(data).length;
    this.searchCriteria = this.filterParams ? data : this.searchCriteria;
    this.loadAll();
  }

  filter(status: ReviewStatus) {
    this.searchCriteria.status = status;
    this.items = this.tempItems.filter((renewal) => {
      return renewal.reviewStatus == status;
    });
    this.searchCriteria.renewalDate = undefined;
    localStorage.setItem('ecList', JSON.stringify(this.searchCriteria));
    this.updateCounter(ReviewStatus[status]);
  }

  loadAll() {
    this.loading = true;
    this.service.getAll().subscribe(items => {
      this.tempItems = (items as ExceptionalCircumstanceListItem[]).sort((a, b) => {
        return new Date(a.submittedAt) > new Date(b.submittedAt) ? -1 : 1;
      });
      this.uniqueRenewalDates = this.tempItems
          .map((item: ExceptionalCircumstanceListItem) => item.renewalDate)
          .filter((date: string, index: number, dates: string[]) => dates.indexOf(date) === index);
      this.items = this.tempItems;
      this.filterList();
      if (this.filterParams === 0) {
        this.clearSearch();
      }
      this.loading = false;
    }, error => {
      this.loading = false;
    });
  }

  toggleSearch() {
    this.searchVisible = !this.searchVisible;
    if (this.searchVisible) {
      setTimeout(() => {
        this.regNumberRef.nativeElement.focus();
      });
    }
  }

  search() {
    this.loading = true;
    this.service.search(this.searchCriteria.regNumber).subscribe((uniqueRegNumber: any) => {
      this.items = [...uniqueRegNumber];
      this.updateCounter(this.searchCriteria.regNumber.toString());
    });
    localStorage.setItem('ecList', JSON.stringify(this.searchCriteria));
    this.loading = false;
  }

  clearSearch() {
    localStorage.removeItem('ecList');
    this.searchCriteria.regNumber = '';
    this.searchCriteria.renewalDate = undefined;
    this.searchCriteria.status = undefined;
    this.items = this.tempItems;
    this.updateCounter('');
  }

  dateChange(date: string) {
    const dateSelectedFromDatePicker = this.utcDatePipe.transform(date);
    this.items = this.tempItems.filter((item: ExceptionalCircumstanceListItem) => {
      return this.utcDatePipe.transform(item.renewalDate).includes(dateSelectedFromDatePicker);
    });
    this.updateCounter(this.dateSelectedByUser ?? '');
  }

  onRenewaldateSelected(date: string) {
    this.dateSelectedByUser = this.utcDatePipe.transform(date);
    this.items = this.tempItems.filter((item: ExceptionalCircumstanceListItem) => {
      const renewalSelectedDate = this.utcDatePipe.transform(item.renewalDate).includes(this.dateSelectedByUser);
      return this.searchCriteria.status ? (item.reviewStatus == this.searchCriteria.status) && renewalSelectedDate : renewalSelectedDate;
    });
    localStorage.setItem('ecList', JSON.stringify(this.searchCriteria));
    this.updateCounter(this.dateSelectedByUser ?? '');
  }

  filterList() {
    this.items = this.tempItems.filter((item: ExceptionalCircumstanceListItem) => {
      return this.searchCriteria.regNumber == item.registrationNumber ||
        this.searchCriteria.status == item.reviewStatus ||
        this.searchCriteria.renewalDate == item.renewalDate;
    });
    this.updateCounter('');
  }
  
  updateCounter(selectedFilter: string) {
    this.numberOfRecordsSelected = this.items.length;
    this.selectedFilter = selectedFilter;
  }

  ngOnDestroy() {
    this.filterList();
  }
}
